import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Loader2, AlertCircle, RefreshCw, BarChart3, Star, MapPin,
  ThumbsUp, Users, MessageSquare, TrendingUp, Cloud, CheckCircle2
} from 'lucide-react';
import { fairManagerApi } from '../services/api';
import { FairManagerFeedbackSummary } from '../types';

/**
 * FairManagerFeedbackDashboard - Aggregate vendor feedback for fair managers (Issue #682)
 *
 * Shows anonymized aggregate metrics from all vendors who submitted post-fair surveys.
 * Helps fair managers understand vendor satisfaction and identify areas for improvement.
 */
export default function FairManagerFeedbackDashboard() {
  const { fairId } = useParams<{ fairId: string }>();
  const [feedback, setFeedback] = useState<FairManagerFeedbackSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadFeedback = useCallback(async (isRefresh = false) => {
    if (!fairId) return;

    try {
      if (isRefresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      const data = await fairManagerApi.getFairVendorFeedback(parseInt(fairId));
      setFeedback(data);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setError(error?.response?.data?.detail || 'Failed to load vendor feedback');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [fairId]);

  useEffect(() => {
    loadFeedback();
  }, [loadFeedback]);

  const handleRefresh = () => {
    loadFeedback(true);
  };

  const formatPercentage = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return 'N/A';
    return `${value.toFixed(1)}%`;
  };

  const getRatingBgColor = (rating: number | null | undefined): string => {
    if (rating === null || rating === undefined) return 'from-gray-50 to-gray-100 border-gray-200';
    if (rating >= 4.5) return 'from-teal-50 to-teal-100 border-teal-200';
    if (rating >= 3.5) return 'from-teal-50 to-moonbone border-teal-200';
    if (rating >= 2.5) return 'from-yellow-50 to-yellow-100 border-yellow-200';
    return 'from-red-50 to-red-100 border-red-200';
  };

  const renderRatingStars = (rating: number | null | undefined) => {
    if (rating === null || rating === undefined) {
      return <span className="text-sm text-gray-400">No ratings yet</span>;
    }

    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    return (
      <div className="flex items-center gap-1">
        {[...Array(5)].map((_, index) => (
          <Star
            key={index}
            className={`w-5 h-5 ${
              index < fullStars
                ? 'fill-yellow-400 text-yellow-400'
                : index === fullStars && hasHalfStar
                ? 'fill-yellow-200 text-yellow-400'
                : 'text-gray-300'
            }`}
          />
        ))}
        <span className="ml-2 text-sm font-semibold text-gray-700">{rating.toFixed(2)}</span>
      </div>
    );
  };

  if (isLoading && !feedback) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  if (error && !feedback) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-red-900 mb-2">Error Loading Feedback</h3>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={() => loadFeedback()}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!feedback) return null;

  if (feedback.total_responses === 0) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-moonbone border border-teal-200 rounded-lg p-8 text-center">
          <MessageSquare className="w-16 h-16 text-secondary mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-ink mb-2">No Feedback Yet</h3>
          <p className="text-primary-dark mb-4">
            No vendors have submitted post-fair surveys for this fair yet.
          </p>
          <p className="text-sm text-secondary">
            Feedback appears here after vendors complete their post-fair surveys.
          </p>
        </div>
      </div>
    );
  }

  const responseRate = (feedback.total_responses / (feedback.total_responses + (feedback.total_reservations - feedback.total_responses))) * 100;

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vendor Feedback</h1>
          <p className="text-gray-600">{feedback.fair_name}</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Response Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-gradient-to-br from-teal-50 to-moonbone border border-teal-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-3 bg-primary rounded-lg">
              <MessageSquare className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-sm font-medium text-ink">Survey Responses</p>
              <p className="text-xs text-primary-dark">Vendor feedback received</p>
            </div>
          </div>
          <p className="text-4xl font-bold text-primary-dark">{feedback.total_responses}</p>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-3 bg-purple-600 rounded-lg">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-sm font-medium text-purple-900">Total Vendors</p>
              <p className="text-xs text-purple-700">Participated in fair</p>
            </div>
          </div>
          <p className="text-4xl font-bold text-purple-700">{feedback.total_reservations}</p>
        </div>

        <div className="bg-gradient-to-br from-teal-50 to-teal-100 border border-teal-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-3 bg-primary rounded-lg">
              <CheckCircle2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-sm font-medium text-teal-900">Response Rate</p>
              <p className="text-xs text-primary-dark">Survey completion</p>
            </div>
          </div>
          <p className="text-4xl font-bold text-primary-dark">{formatPercentage(responseRate)}</p>
        </div>
      </div>

      {/* Average Ratings */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Average Ratings</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className={`bg-gradient-to-br ${getRatingBgColor(feedback.avg_satisfaction_rating)} border rounded-lg p-6`}>
            <div className="flex items-center gap-2 mb-3">
              <Star className="w-5 h-5 text-yellow-500" />
              <h3 className="font-semibold text-gray-900">Overall Satisfaction</h3>
            </div>
            {renderRatingStars(feedback.avg_satisfaction_rating)}
            <p className="text-sm text-gray-600 mt-2">Based on {feedback.total_responses} response{feedback.total_responses !== 1 ? 's' : ''}</p>
          </div>

          <div className={`bg-gradient-to-br ${getRatingBgColor(feedback.avg_booth_location_rating)} border rounded-lg p-6`}>
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="w-5 h-5 text-dusty-teal" />
              <h3 className="font-semibold text-gray-900">Booth Location</h3>
            </div>
            {renderRatingStars(feedback.avg_booth_location_rating)}
            <p className="text-sm text-gray-600 mt-2">Vendor booth placement satisfaction</p>
          </div>

          <div className={`bg-gradient-to-br ${getRatingBgColor(feedback.avg_fair_organization_rating)} border rounded-lg p-6`}>
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className="w-5 h-5 text-purple-500" />
              <h3 className="font-semibold text-gray-900">Fair Organization</h3>
            </div>
            {renderRatingStars(feedback.avg_fair_organization_rating)}
            <p className="text-sm text-gray-600 mt-2">Overall event organization quality</p>
          </div>
        </div>
      </div>

      {/* Vendor Intent & Weather */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <ThumbsUp className="w-5 h-5 text-secondary" />
            <h3 className="text-lg font-semibold text-gray-900">Vendor Return Intent</h3>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-700">Would Return</span>
              <span className="font-semibold text-secondary">{formatPercentage(feedback.would_return_percentage)}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-teal-500 h-3 rounded-full transition-all"
                style={{ width: `${feedback.would_return_percentage || 0}%` }}
              />
            </div>
            <p className="text-sm text-gray-600">
              {Math.round((feedback.would_return_percentage || 0) * feedback.total_responses / 100)} of {feedback.total_responses} vendors said they would return
            </p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Cloud className="w-5 h-5 text-secondary" />
            <h3 className="text-lg font-semibold text-gray-900">Weather Impact</h3>
          </div>
          <div className="space-y-2">
            {feedback.weather_impact_distribution && Object.keys(feedback.weather_impact_distribution).length > 0 ? (
              Object.entries(feedback.weather_impact_distribution).map(([impact, count]) => (
                <div key={impact} className="flex items-center justify-between">
                  <span className="text-gray-700 capitalize">{impact}</span>
                  <span className="font-semibold text-gray-900">{count}</span>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">No weather impact data reported</p>
            )}
          </div>
        </div>
      </div>

      {/* Average Engagement Metrics */}
      {(feedback.avg_foot_traffic || feedback.avg_customer_interactions) && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-secondary" />
            Average Engagement Metrics
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {feedback.avg_foot_traffic !== null && feedback.avg_foot_traffic !== undefined && (
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{Math.round(feedback.avg_foot_traffic)}</p>
                <p className="text-sm text-gray-600 mt-1">Avg Foot Traffic</p>
              </div>
            )}
            {feedback.avg_customer_interactions !== null && feedback.avg_customer_interactions !== undefined && (
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{Math.round(feedback.avg_customer_interactions)}</p>
                <p className="text-sm text-gray-600 mt-1">Avg Interactions</p>
              </div>
            )}
            {feedback.avg_business_cards !== null && feedback.avg_business_cards !== undefined && (
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{Math.round(feedback.avg_business_cards)}</p>
                <p className="text-sm text-gray-600 mt-1">Avg Cards Collected</p>
              </div>
            )}
            {feedback.avg_email_signups !== null && feedback.avg_email_signups !== undefined && (
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{Math.round(feedback.avg_email_signups)}</p>
                <p className="text-sm text-gray-600 mt-1">Avg Email Signups</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Anonymized Comments */}
      {feedback.anonymized_comments && feedback.anonymized_comments.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-secondary" />
            Vendor Comments (Anonymized)
          </h3>
          <div className="space-y-3">
            {feedback.anonymized_comments.map((comment, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg border-l-4 border-primary">
                <p className="text-gray-700 italic">"{comment}"</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Insights */}
      {feedback.avg_satisfaction_rating !== null && feedback.avg_satisfaction_rating !== undefined && (
        <div className="mt-6 bg-moonbone border border-teal-200 rounded-lg p-6">
          <h3 className="font-semibold text-ink mb-2 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Insights
          </h3>
          <ul className="space-y-2 text-sm text-ink">
            {feedback.avg_satisfaction_rating >= 4.5 && (
              <li>• Excellent overall satisfaction! Vendors are very happy with your fair.</li>
            )}
            {feedback.avg_satisfaction_rating < 3.0 && (
              <li>• Satisfaction ratings are below average. Consider reviewing vendor feedback for improvement areas.</li>
            )}
            {feedback.would_return_percentage && feedback.would_return_percentage >= 80 && (
              <li>• High return rate - vendors want to participate in future events!</li>
            )}
            {feedback.avg_booth_location_rating && feedback.avg_booth_location_rating < 3.0 && (
              <li>• Booth location ratings are low. Consider improving booth placement strategy.</li>
            )}
            {feedback.avg_fair_organization_rating && feedback.avg_fair_organization_rating >= 4.5 && (
              <li>• Great job on fair organization - vendors appreciate the effort!</li>
            )}
            {responseRate < 50 && (
              <li>• Consider sending reminder emails to increase survey response rate.</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
